<template>
  <div class="anchor-container">
    <div class="widget-container" v-if="!finished">
      <div v-if="showPromoTag" class="promo-tag" :style="getPromoStyle">
        {{ getPromoLabel() }}
      </div>
      <div
        v-if="!completeAddToCard && product && fromPriceWithSymbol"
        class="price-container"
      >
        <div class="price-line">
          <div class="price-title">
            Price
            <span class="price-tooltip-container">
              <span class="price-from">
                from
              </span>
              <InfoToolTip
                :id="'price-tooltip-top'"
                class="price-tooltip-icon price-tooltip-icon-for-desktop"
                :text="
                  `The base price may change based on real-time availability, prices from our suppliers and promotions.`
                "
                :custom-style="{ height: '24px', weight: '24px' }"
                :variant="'light'"
                :placement="'top'"
              />
              <InfoToolTip
                :id="'price-tooltip-bottom'"
                class="price-tooltip-icon price-tooltip-icon-for-mobile"
                :text="
                  `The base price may change based on real-time availability, prices from our suppliers and promotions.`
                "
                :custom-style="{ height: '24px', weight: '24px' }"
                :variant="'light'"
                :placement="'bottom'"
              />
            </span>
          </div>
          <div class="price-number">
            <span class="dollar-prefix">
              {{ getDollarPrefix() }}
            </span>
            <span :class="{ strikethrough: discountPriceWithSymbol }" class="dollar-price">{{ fromPriceWithSymbol }}</span>
            <span v-if="discountPriceWithSymbol" class="discounted-price dollar-price">{{ discountPriceWithSymbol }}</span>
          </div>
        </div>

        <div class="free-cancellation">
          <div class="free-cancellation-icon"></div>
          <span>Free Cancellation</span>
        </div>
      </div>
    </div>
    <div v-if="product" class="widget-container ventrata-section">
      <VentrataBookingWidget
        v-if="!finished"
        :product="product"
        @addToCart="onAddToCart"
      />
      <VentrataBookingCheckoutWidget
        v-else
        :tours="tour.toursBookedTogetherCart ? tour.toursBookedTogetherCart : []"
      />
    </div>
    <div v-else>
      Product not found
    </div>
    <CustomSkeleton v-if="loading" class="ventrata-loading" />
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { getFromPrice, addCurrencySymbol, getPrefixSymbol } from '@/helpers/price'
import { showTourPromoBanner, getTodayStr, getStartOfMonthStr, getEndOfMonthStr } from '@/helpers/main'
import { getAvailabilityPayload, getAdultPricing } from '@/helpers/ventrata'
import InfoToolTip from '@/components/utils/InfoToolTip'
import VentrataBookingWidget from '@/components/utils/ventrata/VentrataBookingWidget'
import VentrataBookingCheckoutWidget from '@/components/utils/ventrata/VentrataBookingCheckoutWidget'
import CustomSkeleton from '@/components/utils/basic/CustomSkeleton'

const dateFMT = 'YYYY-MM-DD'

export default {
  name: 'BookingWidget',
  components: {
    InfoToolTip,
    CustomSkeleton,
    VentrataBookingWidget,
    VentrataBookingCheckoutWidget
  },
  props: {
    product: {
      type: Object,
      require: true
    },
    promo: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      finished: false,
      loading: false,
      // flag + not mobile
      completeAddToCard: false,
      isPopChanged: false
    }
  },
  computed: {
    ...mapGetters({
      tour: "tour",
      currency: "currency",
      unavailabile: "unavailabile",
      initialAvailabilities: 'ventrata/initAvailabilities',
    }),
    getPromoStyle () {
      if (this.showPromoTag && this.promo?.promoTag) {
        return `
          background-color: ${this.promo.promoTag.bgColor};
          color: ${this.promo.promoTag.textColor};
        `
      } else {
        return ''
      }
    },
    showPromoTag () {
      if (this.unavailabile) {
        return false
      }
      return showTourPromoBanner(this.promo, 'Product Page')
    },
    fromPriceWithSymbol () {
      if (!this.basePrice) {
        return
      }
      const result = addCurrencySymbol(this.currency, this.basePrice)
      this.$emit('updatePrice', result)
      return result
    },
    basePrice () {
      if (!this.product) {
        return
      }
      return getFromPrice(this.product, this.currency)
    },
    lowestPrice () {
      if (!this.initialAvailabilities) {
        return null
      }
      const validAvailabilities = this.initialAvailabilities.filter(obj => obj.available)
      const validatePriceList = validAvailabilities.map(obj => {
        const adultPricing = getAdultPricing(obj)
        return adultPricing?.original ?? 0
      })
      // console.log('validatePriceList :>> ', validatePriceList)
      if (validatePriceList?.length > 0) {
        const lowestOriginal = Math.min(...validatePriceList)
        console.log('lowestOriginal :>> ', lowestOriginal)
        return lowestOriginal / 100
      }
      return null
    },
    discountPriceWithSymbol () {
      if (this.loading) {
        return
      }
      if (!this.lowestPrice || !this.basePrice || this.lowestPrice >= this.basePrice ) {
        return
      }
      const result = addCurrencySymbol(this.currency, this.lowestPrice)
      this.$emit('updateDiscountPrice', result)
      return result
    }
  },
  watch: {
    currency (to, from) {
      // this.reRenderSDK()
      if (this.product) {
        this.getAvailabilitiesForProduct()
      }
    }
  },
  mounted () {
    // console.log('this.tour: ', this.tour)
    this.loading = true
    this.$store.commit('ventrata/setInitAvailabilities', null)
    if (this.product) {
      this.getAvailabilitiesForProduct()
    }
    window.addEventListener('popstate', this.onPopState)
  },
  unmounted () {
    window.removeEventListener('popstate', this.onPopState)
  },
  methods: {
    getAvailabilitiesForProduct() {
      this.loading = true
      this.getAvailabilityMonthByMonth().then((allAvails) => {
        // console.log('allAvails :>> ', allAvails?.length)
        const processedAvailabilities = allAvails?.filter(a => a.available)
        this.$store.commit('ventrata/setInitAvailabilities', processedAvailabilities)
        if (!processedAvailabilities || processedAvailabilities.length <= 0) {
          this.$emit('notAvailable')
        }
      }).finally(() => {
        this.loading = false
      })
    },

    async getAvailabilityMonthByMonth () {
      const promises = []
      // Generate the next 7 months
      for (let i = 0; i < 7; i++) {
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() + i)
        const startDateStr = i === 0 ? getTodayStr(dateFMT) : getStartOfMonthStr(startDate, dateFMT)
        
        const endDate = new Date(startDate)
        endDate.setMonth(endDate.getMonth() + 1)
        endDate.setDate(0) // Last day of the month
        const endDateStr = getEndOfMonthStr(startDate, dateFMT)
        
        const availabilityPayload = getAvailabilityPayload(
          this.product, 
          startDateStr, 
          endDateStr, 
          this.currency
        )
        // console.log('availabilityPayload :>> ', availabilityPayload)
        // Add the promise to our array
        promises.push(
          this.$store.dispatch('ventrata/getAvailability', availabilityPayload)
        )
      }
      // Wait for all promises to resolve
      const allAvailabilities = []
      await Promise.all(promises)
        .then(responses => {
          // Filter out error responses and flatten the array
          const availabilities = responses
            .filter(res => res?.result !== 'error')
            .flatMap(res => res?.filter(a => a.available) || [])
            // console.log('availabilities :>> ', availabilities)
            allAvailabilities.push(...availabilities)
        })
        .catch(error => {
          console.error('Error fetching availabilities:', error)
        })
      const sortedAvailabilities = allAvailabilities.sort((a, b) => {
        if (a.id < b.id) return -1
        if (a.id > b.id) return 1
        return 0
      })
      return sortedAvailabilities
    },
    // Here, listening for the popstate event is to allow the order page to actively refresh when returning. The current macro task patch is to prevent recursive deadlock issues caused by hashchange.
    onPopState (e) {
      console.log('onPopState :>> ', e)
      if (!this.isPopChanged) {
        this.isPopChanged = true
        setTimeout(() => {
          this.isPopChanged = false
        }, 0)
        location.replace(location.href)
      }
    },
    onAddToCart (status, bookingId, order) {
      this.finished = status
      history.pushState(null, '')
      this.$store.commit('ventrata/addTourInfoToCart', {
        bookingId,
        tourInfo: this.tour,
        order
      })
      // TODO add tour info to related booking, handle in store
      localStorage.setItem('tour', JSON.stringify(this.tour))
    },
    getSlugRef (slug) {
      return '/' + slug + '/'
    },
    getDollarPrefix () {
      return getPrefixSymbol(this.currency)
    },
    getPromoLabel () {
      if (this.promo && this.promo.promoTag) {
        return this.promo.promoTag.salesText
      } else {
        return ''
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.anchor-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 360px;
  max-width: 360px;
  margin-left: auto;

  .ventrata-loading {
    position: absolute;
    inset: 0;
    z-index: 999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: $bg-grey;
  }


  .price-tooltip-container {
    position: relative;
    .price-from {
      margin-left: 8px;
    }
    .price-tooltip {
      padding-left: 4px;
      font-size: 22px;
    }
    .price-tooltip-icon {
      position: absolute;
      top: 0;
    }
  }

  .price-tooltip-icon-for-mobile {
    display: none;
  }

  .price-tooltip-icon-for-desktop {
    display: inline-block;
  }

  .free-cancellation {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    text-align: right;
    padding-top: 8px;
    .free-cancellation-icon {
      display: inline-block;
      background: $woi-gold-main;
      mask-image: url('@/assets/svg/check-circle.svg');
      mask-size: cover;
      width: 24px;
      height: 24px;
    }

    span {
      vertical-align: middle;
      margin-left: 4px;
      color: $woi-gold-main;
      @extend %paragraph-1;
      font-family: Poppins;
    }
  }
  .price-line {
    @include font__header-5();
    color: $text-color;
    .price-title {
      flex: 1;
    }
    .price-number {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      text-align: right;
      color: $text-color;
    }
  }
  .widget-container .price-container {
    width: 100%;
    padding: 24px;
    display: flex;
    flex-direction: column;
    background-color: rgb(247, 247, 247);
    border-radius: 4px;
    .price-line {
      display: flex;
      align-items: flex-end;
    }
  }
  .widget-container,
  .book {
    width: 100%;
  }
  .book #hb-commerce-sdk-root-desk ::v-deep {
    &.zoid-outlet {
      height: 102% !important;
    }
  }
}

.ventrata-section {
  margin-top: 16px;
  width: 100%;
}

@include max-width($tablet) {
  .book,
  .widget-container {
    max-width: 410px;
  }
  .anchor-container {
    margin: 0 auto;
    .price-tooltip-icon-for-mobile {
      display: inline-block;
    }

    .price-tooltip-icon-for-desktop {
      display: none;
    }
  }
}

.hiddenForCheck {
  visibility: hidden;
  height: 0;
}

@include max-width($tablet-sm - 1px) {
  .anchor-container {
    margin: 0 auto;
    .widget-container {
      max-width: auto;
      .price-container {
        padding: 16px 8px;
      }
    }

    .price-line {
      .price-title {
        .price-from {

        }
      }
    }
  }
  .ventrata-section {

  }
  .book {
    max-width: 362px;
  }
  .widget-container.hideOnMobile {
    display: none;
  }
  .hiddenForCheck {
    visibility: visible;
  }
}

.promo-tag {
  width: 100%;
  padding: 12px;
  text-align: center;
  background-color: #00ab68;
  color: white;
  border-radius: 24px;
  margin-bottom: 16px;
  font-weight: 700 !important;
}

.check-availability-button {
  background: $woi-gold-main;
  border-radius: 90px;
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  color: #f7f7f7;
  padding: 10px;
  margin: 10px;
  text-align: center;
  cursor: pointer;
}
</style>
